class WebSocketServiceClass {
  private ws: WebSocket | null = null;
  private messageHandlers: ((message: any) => void)[] = [];
  private connectionHandlers: ((connected: boolean) => void)[] = [];
  private isConnected = false;
  private sessionId: string | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  connect(projectId: string, userId: string, authToken?: string, forceReconnect = false) {
    if (this.ws?.readyState === WebSocket.OPEN && !forceReconnect) return;

    if (forceReconnect && this.ws) {
        this.disconnect();
    }

    const token = authToken || localStorage.getItem('stan_token');
    if (!token) {
      console.error('No auth token found, cannot connect to agent');
      return;
    }

    this.sessionId = `ide_${Date.now()}_${userId.substring(0, 8)}`;
    const wsUrl = `wss://stan-backend.stanleyisaac134.workers.dev/agents/ChatCoordinatorAgent/${this.sessionId}`;

    console.log('🔌 Connecting to ChatCoordinatorAgent:', wsUrl);

    this.ws = new WebSocket(wsUrl);

    this.ws.onopen = () => {
      console.log('✅ WebSocket connected');
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.connectionHandlers.forEach(handler => handler(true));

      // Send init message
      this.send({
        type: 'init',
        userId,
        projectId,
        source: 'ide',
      });
    };

    this.ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        // Filter out pong/keepalive to reduce noise
        if (message.type !== 'pong') {
            console.log('📨 Received:', message.type);
        }
        this.messageHandlers.forEach(handler => handler(message));
      } catch (error) {
        console.error('Failed to parse message:', error);
      }
    };

    this.ws.onclose = () => {
      console.log('🔌 WebSocket closed');
      this.isConnected = false;
      this.connectionHandlers.forEach(handler => handler(false));

      // Auto-reconnect
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++;
        console.log(`🔄 Auto-reconnecting attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}...`);
        setTimeout(() => this.connect(projectId, userId, token), 2000 * this.reconnectAttempts);
      }
    };

    this.ws.onerror = (error) => {
      console.error('❌ WebSocket error:', error);
      this.messageHandlers.forEach(handler => handler({
        type: 'error',
        message: 'Connection error'
      }));
    };
  }

  reconnect(projectId: string, userId: string) {
      console.log('🔄 Manual reconnect triggered');
      this.reconnectAttempts = 0; // Reset attempts for manual retry
      this.connect(projectId, userId, undefined, true);
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
      this.isConnected = false;
    }
  }

  // Alias for compatibility
  send(message: any) {
    this.sendMessage(message);
  }

  sendMessage(message: any) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket not connected, queuing message or dropping');
      return;
    }

    console.log('📤 Sending:', message.type);
    this.ws.send(JSON.stringify(message));
  }

  requestFix(projectId: string, userId: string, filePath: string, error: string) {
    this.send({
      type: 'request_fix',
      projectId,
      userId,
      filePath,
      error
    });
  }

  saveFile(projectId: string, userId: string, filePath: string, content: string) {
    this.send({
      type: 'save_file',
      projectId,
      userId,
      filePath,
      content
    });
  }

  onMessage(handler: (message: any) => void) {
    this.messageHandlers.push(handler);
    return () => {
      const index = this.messageHandlers.indexOf(handler);
      if (index > -1) {
        this.messageHandlers.splice(index, 1);
      }
    };
  }

  onConnectionChange(handler: (connected: boolean) => void) {
    this.connectionHandlers.push(handler);
    return () => {
      const index = this.connectionHandlers.indexOf(handler);
      if (index > -1) {
        this.connectionHandlers.splice(index, 1);
      }
    };
  }

  get connected() {
    return this.isConnected;
  }
}

export const WebSocketService = new WebSocketServiceClass();